import { useState, useEffect, useMemo } from "react";
import { Location, BilletGroup } from "../../../../models";
import { getLocationsByIds } from "../../../../repository/BilletYardInventoryRepository";
import { parseLocationsResponse } from "../../../../utils/parseLocationsResponse";

/**
 * Process groups to mark all groups as selectable (top-of-pile logic disabled for testing)
 * WARNING: This allows selecting any billet, which may cause invalid move operations
 */
const processGroupsForTopOfPile = (groups: BilletGroup[]): BilletGroup[] => {
  if (groups.length === 0) return [];

  // Mark ALL groups as selectable for testing purposes
  return groups.map((group) => ({
    ...group,
    IsSelected: true, // Allow selection of any billet
  }));
};

interface ModalDataState {
  isLoading: boolean;
  sourceLocation: Location | null;
  destinationLocation: Location | null;
  sourceGroups: BilletGroup[];
  destinationGroups: BilletGroup[];
  hasError: boolean;
  errorMessage: string;
  lastSourceUpdate: number;
  lastDestinationUpdate: number;
}

interface UseModalDataProps {
  sourceLocationId: number | null;
  destinationLocationId: number | null;
}

export const useSpecificLocationsData = ({
  sourceLocationId,
  destinationLocationId,
}: UseModalDataProps) => {
  const [state, setState] = useState<ModalDataState>({
    isLoading: true,
    sourceLocation: null,
    destinationLocation: null,
    sourceGroups: [],
    destinationGroups: [],
    hasError: false,
    errorMessage: "",
    lastSourceUpdate: 0,
    lastDestinationUpdate: 0,
  });

  const fetchModalData = async (isRefresh = false) => {
    if (!sourceLocationId || !destinationLocationId) {
      setState((prev) => ({ ...prev, isLoading: false }));
      return;
    }

    // Only show loading spinner on initial load, not on refresh
    if (!isRefresh) {
      setState((prev) => ({ ...prev, isLoading: true, hasError: false }));
    }

    try {
      const response = await getLocationsByIds(
        sourceLocationId,
        destinationLocationId,
      );

      const parsedData = parseLocationsResponse(response);
      if (parsedData) {
        // Find source and destination locations from the parsed data
        const allLocations = [
          ...parsedData.locations,
          ...parsedData.specialLocations,
        ];
        const sourceLocation = allLocations.find(
          (loc: Location) => loc.LocationID === sourceLocationId,
        );
        const destinationLocation = allLocations.find(
          (loc: Location) => loc.LocationID === destinationLocationId,
        );

        if (sourceLocation && destinationLocation) {
          // Process groups to identify top-of-pile groups (highest position per GroupName)
          const sourceGroups = processGroupsForTopOfPile(
            parsedData.groupsByLocationId[sourceLocationId] || [],
          );
          const destinationGroups = processGroupsForTopOfPile(
            parsedData.groupsByLocationId[destinationLocationId] || [],
          );

          setState((prev) => ({
            ...prev,
            isLoading: false,
            sourceLocation,
            destinationLocation,
            sourceGroups,
            destinationGroups,
            hasError: false,
            errorMessage: "",
          }));
        } else {
          throw new Error("Could not find both locations in response");
        }
      } else {
        throw new Error("Invalid response format or insufficient data");
      }
    } catch (error: unknown) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        hasError: true,
        errorMessage:
          error instanceof Error
            ? error.message
            : "Failed to load location data",
      }));
    }
  };

  useEffect(() => {
    fetchModalData();
  }, [sourceLocationId, destinationLocationId]);

  // Update specific location data
  const updateLocationData = (
    locationId: number,
    groups: BilletGroup[],
    location?: Location,
  ) => {
    const now = Date.now();

    // Process groups to identify top-of-pile groups
    const processedGroups = processGroupsForTopOfPile(groups);

    setState((prev) => {
      if (locationId === sourceLocationId) {
        return {
          ...prev,
          sourceGroups: processedGroups,
          sourceLocation: location || prev.sourceLocation,
          lastSourceUpdate: now,
        };
      } else if (locationId === destinationLocationId) {
        return {
          ...prev,
          destinationGroups: processedGroups,
          destinationLocation: location || prev.destinationLocation,
          lastDestinationUpdate: now,
        };
      }
      return prev;
    });
  };

  // Optimistic update function for smoother billets movement
  const updateBilletsOptimistically = (
    sourceGroupId: number,
    billetsCount: number,
  ) => {
    setState((prev) => {
      const newSourceGroups = prev.sourceGroups
        .map((group) => {
          if (group.LocationContentID === sourceGroupId) {
            return {
              ...group,
              BilletsCount: Math.max(0, group.BilletsCount - billetsCount),
            };
          }
          return group;
        })
        .filter((group) => group.BilletsCount > 0);

      return {
        ...prev,
        sourceGroups: newSourceGroups,
        lastSourceUpdate: Date.now(),
      };
    });
  };

  // Refresh both locations data efficiently in a single call
  const refreshBothLocationsData = async () => {
    if (!sourceLocationId || !destinationLocationId) return;

    try {
      const response = await getLocationsByIds(
        sourceLocationId,
        destinationLocationId,
      );
      const parsedData = parseLocationsResponse(response);

      if (parsedData) {
        const allLocations = [
          ...parsedData.locations,
          ...parsedData.specialLocations,
        ];
        const sourceLocation = allLocations.find(
          (loc: Location) => loc.LocationID === sourceLocationId,
        );
        const destinationLocation = allLocations.find(
          (loc: Location) => loc.LocationID === destinationLocationId,
        );

        // Update both locations in a single state update to prevent multiple re-renders
        const now = Date.now();
        setState((prev) => ({
          ...prev,
          sourceLocation: sourceLocation || prev.sourceLocation,
          destinationLocation: destinationLocation || prev.destinationLocation,
          sourceGroups: processGroupsForTopOfPile(
            parsedData.groupsByLocationId[sourceLocationId] || [],
          ),
          destinationGroups: processGroupsForTopOfPile(
            parsedData.groupsByLocationId[destinationLocationId] || [],
          ),
          lastSourceUpdate: now,
          lastDestinationUpdate: now,
        }));
      }
    } catch (error) {
      /* Error handled silently */
    }
  };

  // Check if there are billets to move
  const hasBilletsToMove = useMemo(() => {
    if (state.isLoading || state.hasError) return false;

    const sourceHasBillets = state.sourceGroups.some(
      (group) => group.BilletsCount > 0,
    );
    const destinationHasBillets = state.destinationGroups.some(
      (group) => group.BilletsCount > 0,
    );

    return sourceHasBillets || destinationHasBillets;
  }, [
    state.sourceGroups,
    state.destinationGroups,
    state.isLoading,
    state.hasError,
  ]);

  return {
    ...state,
    hasBilletsToMove,
    refreshModalData: (isRefresh = true) => fetchModalData(isRefresh),
    updateBilletsOptimistically,
    updateLocationData,
    refreshBothLocationsData,
  };
};
